import { useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";

import {
  formatIsoDate,
  renderStatus,
  formatName,
  isProgramChairPositionName,
} from "../../utils/generalUtils";

import GeneratePdf from "../../generatePdf/GeneratePdf";

import SpectatorTable from "../../components/Table/SpectatorTable";
import useSpectateCwaService from "../../services/Spectator/spectateCwaService";
import usePositionService from "../../services/PositionAdmin/positionService";
import { Input, Select, Space } from "antd";

const { Search } = Input;

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatAbsentDatesLabel(absentDates) {
  if (!Array.isArray(absentDates) || absentDates.length === 0) return "None";
  // New submissions store a single free-text string; old ones store ISO date strings.
  if (!/^\d{4}-\d{2}-\d{2}$/.test(absentDates[0])) {
    return absentDates[0] || "None";
  }
  return absentDates
    .map((ds) => {
      const d = new Date(ds + "T00:00:00");
      return `${SHORT_MONTHS[d.getMonth()]} ${d.getDate()}`;
    })
    .join(", ");
}

function formatAccomplishmentsLabel(accomplishments) {
  if (!Array.isArray(accomplishments) || accomplishments.length === 0)
    return "—";
  return (
    <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 240, overflowY: "auto" }}>
      {accomplishments.map((a, i) => <li key={i}>{a}</li>)}
    </ul>
  );
}

const AllCwa = () => {
  const { getCwaFromAppId, cwaLoading, cwaResponse } = useSpectateCwaService();
  const { getAllApprovers, approverResponse } = usePositionService();
  const { adminAllCwaPopup } = useSelector((state) => state.generalSlice);

  const mappedApprovers = useMemo(() => {
    // Program Chairs only approve FSR — CWA is Dean-level (or non-faculty
    // approver) territory, so they're excluded here.
    return (approverResponse?.data || [])
      .filter((item) => !isProgramChairPositionName(item?.position_name))
      .map((item) => ({
        value: item?.id,
        label: `${item?.position_name} - ${item?.unit}`,
      }));
  }, [approverResponse]);

  const [selectedPos, setSelectedPos] = useState();
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    getAllApprovers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedPos && mappedApprovers.length > 0) {
      const first = mappedApprovers[0].value;
      setSelectedPos(first);
      getCwaFromAppId(first);
    }
  }, [mappedApprovers, selectedPos, getCwaFromAppId]);

  const handlePosSelect = (posId) => {
    setSelectedPos(posId);
    getCwaFromAppId(posId);
  };

  const allMappedData =
    cwaResponse?.data?.map((item, index) => ({
      key: index + 1,
      submitter: `${item?.submission?.submitter?.last_name || ""}, ${
        item?.submission?.submitter?.first_name || ""
      }`.trim(),
      formattedName: formatName(
        item?.submission?.submitter?.first_name,
        item?.submission?.submitter?.middle_name,
        item?.submission?.submitter?.last_name,
        "lfmcut"
      ),
      approver_name: `${item?.approver?.last_name || ""}, ${
        item?.approver?.first_name || ""
      }`.trim(),
      formattedApproverName: formatName(
        item?.approver?.first_name,
        item?.approver?.middle_name,
        item?.approver?.last_name,
        "lfmcut"
      ),
      ...item?.submission?.certificate_of_work_accomplishment,
      submitter_position: item?.submission?.position?.position_name,
      submitter_position_unit: item?.submission?.position?.unit,
      date_submitted: item?.submission?.created_at
        ? formatIsoDate(item?.submission?.created_at)
        : null,
      status: item?.status || "no data",
      submission_id: item?.submission?.id,
      approval_id: item?.id,
      approval_date: item?.approval_date,
    })) || [];

  const mappedData = searchValue
    ? allMappedData.filter((item) =>
        [item.submitter, item.approver_name]
          .filter(Boolean)
          .some((val) => val.toLowerCase().includes(searchValue.toLowerCase()))
      )
    : allMappedData;

  const columns = [
    {
      title: "No.",
      dataIndex: "key", width: 60,
      rowScope: "row",
    },
    {
      title: "Submitter",
      dataIndex: "submitter",
      key: "submitter",
    },
    {
      title: "Approver",
      dataIndex: "approver_name",
      key: "approver_name",
    },
    {
      title: "Start Date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "End Date",
      dataIndex: "end_date",
      key: "end_date",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: renderStatus(
        ["pending"],
        ["approved"],
        ["disapproved", "denied"],
        ["cancelled"]
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, item) => {
        return (
          <GeneratePdf
            docuType="cwa"
            contents={item}
            disabled={item?.status !== "approved"}
          />
        );
      },
    },
  ];

  const viewingDataConfig = [
    {
      key: 1,
      collapseName: "Period",
      collapseItems: [
        { key: 1, name: "start_date", label: "Start Date" },
        { key: 2, name: "end_date", label: "End Date" },
        {
          key: 3,
          name: "absent_dates",
          label: "Dates of Absence",
          render: (val) => formatAbsentDatesLabel(val),
        },
      ],
    },
    {
      key: 2,
      collapseName: "Accomplishments",
      collapseItems: [
        {
          key: 1,
          name: "accomplishments",
          label: "Accomplishments",
          render: (val) => formatAccomplishmentsLabel(val),
        },
      ],
    },
  ];

  return (
    <>
      {cwaLoading && "loading..."}
      {!cwaLoading && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <Space>
              <p>See submissions under: </p>
              <Select
                defaultValue={selectedPos}
                options={mappedApprovers}
                onChange={handlePosSelect}
                popupMatchSelectWidth={false}
              />
            </Space>
            <Search
              placeholder="Search submitter or approver..."
              value={searchValue}
              size="large"
              allowClear
              enterButton
              onSearch={setSearchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ maxWidth: 400 }}
            />
          </div>
          <SpectatorTable
            tableColumn={columns}
            tableData={mappedData}
            popupSlice={adminAllCwaPopup}
            popupName="adminAllCwaPopup"
            docuType="Certificate of Work Accomplishment"
            viewingDataConfig={viewingDataConfig}
          />
        </>
      )}
    </>
  );
};

export default AllCwa;
